import apiClient from '@/lib/api/apiClient'
import type {
  PmcPropertyMappingOption,
  PmsAuditRow,
  PropertyMappingReview,
  PropertyMappingRow,
  PropertyMatchingRunSummary,
} from '../types'

function unwrap<T>(raw: unknown): T {
  let value = raw
  for (let depth = 0; depth < 4; depth += 1) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) break
    const object = value as Record<string, unknown>
    const nested = object.data ?? object.result ?? object.payload
    if (nested == null) break
    value = nested
  }
  return value as T
}

interface ApiPmc {
  pmc_id: number
  company_id: number
  company_name: string
  property_mapping_enabled: boolean
  property_count: number
}

interface ApiPropertyMappingRow {
  id: number
  pmc_id: number
  pmc_name: string
  source_property_code: string
  source_property_name: string
  source_address: string
  liberty_property_id?: number
  liberty_property_name?: string
  liberty_address?: string
  status: PropertyMappingRow['status']
  mapping_method?: PropertyMappingRow['mappingMethod']
  confidence?: number | null
  match_reason?: string | null
  admin_note?: string | null
  last_checked_at?: string | null
}

export async function fetchPropertyMappingPmcs(): Promise<PmcPropertyMappingOption[]> {
  const { data } = await apiClient.get('/api/pms-property-matching/pmcs')
  const rows = unwrap<ApiPmc[]>(data)
  if (!Array.isArray(rows)) return []

  return rows.map((row) => ({
    pmcId: String(row.pmc_id),
    companyId: row.company_id,
    companyName: row.company_name,
    propertyMappingEnabled: Boolean(row.property_mapping_enabled),
    propertyCount: Number(row.property_count || 0),
  }))
}

export async function updatePmcPropertyMappingStatus(
  pmcId: string | number,
  enabled: boolean,
): Promise<void> {
  await apiClient.post(`/api/pms-property-matching/pmcs/${pmcId}/status`, { enabled })
}

export async function fetchPmcPropertyMappings(params: {
  pmc_id: string | number
}): Promise<PropertyMappingRow[]> {
  const { data } = await apiClient.get('/api/pms-property-matching/properties', { params })
  const rows = unwrap<ApiPropertyMappingRow[]>(data)
  if (!Array.isArray(rows)) return []

  return rows.map((row) => ({
    id: row.id,
    pmcId: String(row.pmc_id),
    pmcName: row.pmc_name,
    sourcePropertyCode: row.source_property_code,
    sourcePropertyName: row.source_property_name,
    sourceAddress: row.source_address,
    libertyPropertyId: row.liberty_property_id,
    libertyPropertyName: row.liberty_property_name,
    libertyAddress: row.liberty_address,
    status: row.status,
    mappingMethod: row.mapping_method,
    confidence: row.confidence ?? undefined,
    matchReason: row.match_reason || undefined,
    adminNote: row.admin_note || undefined,
    lastCheckedAt: row.last_checked_at || 'Not run yet',
  }))
}

interface ApiReviewCandidate {
  property_id: number
  property_name: string
  address: string
  name_match: boolean
  score: number
  flags?: Record<string, boolean>
  reasons?: string[]
}

interface ApiHistoryEvent {
  property_mapping_history_id: number
  event_type: string
  note?: string | null
  created_at: string
}

export async function fetchPropertyMappingReview(
  mappingId: number,
  search?: string,
): Promise<PropertyMappingReview> {
  const { data } = await apiClient.get(
    `/api/pms-property-matching/properties/${mappingId}/review`,
    { params: { search: search || undefined } },
  )
  const result = unwrap<{
    candidates?: ApiReviewCandidate[]
    history?: ApiHistoryEvent[]
  }>(data)

  return {
    candidates: (result.candidates || []).map((candidate) => ({
      propertyId: candidate.property_id,
      propertyName: candidate.property_name,
      address: candidate.address,
      nameMatch: Boolean(candidate.name_match || candidate.flags?.name_exact),
      score: Number(candidate.score || 0),
      flags: {
        nameExact: Boolean(candidate.flags?.name_exact),
        nameNear: Boolean(candidate.flags?.name_near),
        aliasExact: Boolean(candidate.flags?.alias_exact),
        aliasNear: Boolean(candidate.flags?.alias_near),
        addressExact: Boolean(candidate.flags?.address_exact),
        addressNear: Boolean(candidate.flags?.address_near),
        streetMatch: Boolean(candidate.flags?.street_match),
        cityMatch: Boolean(candidate.flags?.city_match),
        stateMatch: Boolean(candidate.flags?.state_match),
        zipMatch: Boolean(candidate.flags?.zip_match),
      },
      reasons: Array.isArray(candidate.reasons) ? candidate.reasons : [],
    })),
    history: (result.history || []).map((event) => ({
      id: event.property_mapping_history_id,
      eventType: event.event_type,
      note: event.note || undefined,
      timestamp: event.created_at,
    })),
  }
}

export async function updatePropertyMappingStatus(
  mappingId: number,
  payload:
    | { status: 'MAPPED'; property_id: number; note?: string }
    | { status: 'SKIPPED'; note?: string }
    | { status: 'NEEDS_REVIEW'; note?: string },
): Promise<void> {
  await apiClient.post(`/api/pms-property-matching/properties/${mappingId}/status`, payload)
}

export async function fetchPropertyMappingAudit(params?: {
  pmc_id?: string | number
  company_id?: string | number
}): Promise<PmsAuditRow[]> {
  const { data } = await apiClient.get('/api/pms-property-matching/audit', { params })
  const rows = unwrap<PmsAuditRow[]>(data)
  return Array.isArray(rows) ? rows : []
}

export async function savePropertyMappingAuditNote(payload: {
  note: string
  pmc_id?: string | number
  company_id?: string | number
}): Promise<void> {
  await apiClient.post('/api/pms-property-matching/audit', payload)
}

export async function runPropertyMatchingAutomation(): Promise<PropertyMatchingRunSummary> {
  const { data } = await apiClient.post('/api/pms-property-matching/run')
  const result = unwrap<{
    pmcs_processed: number
    properties_processed: number
    auto_mapped: number
    needs_review: number
  }>(data)

  return {
    pmcsProcessed: Number(result.pmcs_processed || 0),
    propertiesProcessed: Number(result.properties_processed || 0),
    autoMapped: Number(result.auto_mapped || 0),
    needsReview: Number(result.needs_review || 0),
  }
}
